import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';

import { DEFAULT_TIME_FORMAT } from '../../../../common/constants';
import DatePicker    from '../../../../components/DatePicker';
import Icons         from '../../../../icons';
import EditableHeading    from '../../../../components/EditableHeading';
import ConfirmationTextBox from "../../../../components/ConfirmationPrompt";
import DiaryDetailPopUp   from '../../../../components/DiaryDetailPopUp';

import { getResolutionName } from '../../utils';
import { DIARY_CANCELLED_TEXT, StringConstants, CharConstants } from "../../constants";
import { createDiaryUrl } from "../../uri";

import {
    changeDiarieTitle, changeDiarieDueDate, showTooltip, hideTooltip,
    getActivityDetail, discardNoteRedirectUrl,
} from '../../actions/action';
import { showDialogueBox, hideDialogueBox } from '../../../../components/DialogueBox/actions';
import { resetViewDiaryInfo, updateDiary } from "../../../Diaries/actions";

// Sub-components
const CustomDueDateLabel = ({ onClick, data }) => (
    <button
        type='button'
        onClick={onClick}
        className={`rowFlex dueDate ${data.is_overdue && !data.is_due_today ? "red" : "blue"}`}
    >
        {data.due_on}
    </button>
);

const ModuleBadge = ({ module, wkNum, actTitle, moduleId, onActivityClick }) => {
    if (module === StringConstants.workkard) {
        return (
            <>
                <span className="badges wkSymbol mr">{CharConstants.Workkard}</span>
                <label>{wkNum}</label>
            </>
        );
    }
    return (
        <>
            <span className="badges actSymbol mr">{CharConstants.Activity}</span>
            <label className="mrNone">
                <a href="#" onClick={onActivityClick}>{actTitle}</a>
            </label>
        </>
    );
};

const TagList = ({ tags }) => {
    if (!tags?.length) return null;
    return tags.slice().sort().map((tag, i) => (
        <label key={i} className="mr status tags">{tag.toUpperCase()}</label>
    ));
};

const ActionMenu = ({ visible, data, onToggle, onReopen, onCancel, isCompleted }) => {
    if (data.status === StringConstants.cancelled) return null;

    return (
        <div className={`dropDown animated noteAction superFaste ${visible ? "active" : ""}`}>
            <span className="icon" onClick={onToggle}>
                <Icons name="more" className="icon more" />
            </span>
            <div className={`dropDownMenu openRight ${visible ? "onClick" : ""}`}>
                {data.can_complete_or_reopen && isCompleted && data.status !== StringConstants.open && (
                    <a className="completeDiary pointer" onClick={onReopen}>Re-open</a>
                )}
                {data.is_deleteable && (
                    <a className="deleteDiary pointer" onClick={onCancel}>Cancel Diary</a>
                )}
            </div>
        </div>
    );
};

const DiarieCell = ({ data }) => {
    const dispatch = useDispatch();

    const workkard            = useSelector(s => s.workkardDetail.workkardDetail);
    const activityDetail      = useSelector(s => s.workkardDetail.activityDetail);
    const isNoteCreationVisible = useSelector(s => s.workkardDetail.panelVisibility.isNoteCreationVisible);
    const sessionContext      = useSelector(s => s.sessionContextOidc);
    const userPermissions = sessionContext.userView.permissions;
    const {can_edit_todo, can_edit_own_todo} = userPermissions;    

    const selectedDate = useRef(moment());
    const [dueDate,          setDueDate]          = useState(() => moment(data.due_date));
    const [actionMenuVisible, setActionMenuVisible] = useState(false);

    useEffect(() => {
        setDueDate(moment(data.due_date));
    }, [data.due_date]);

    // Derived
    const isActiveUser = (data?.owner?.user_id == sessionContext.userView.id) && sessionContext?.userView?.isActive    
    const canComplete = data.can_complete_or_reopen && userPermissions?.can_completeorreopen_diary && data.status !== StringConstants.cancelled;
    const isCompleted = data?.status == "COMPLETED";

    const isOwner = data?.owner?.user_id === sessionContext?.userView?.id;
    const isEditable = (can_edit_own_todo && isOwner) || (can_edit_todo && !isOwner);

    // Handlers
    const handleDateChange = useCallback((dateObj) => {
        selectedDate.current = dateObj;
    }, []);

    const handleDueDateChange = useCallback((comment) => {
        dispatch(changeDiarieDueDate({
            cid:            data.todo_id,
            tz:             data.due_date_tz,
            comment,
            updatedDuedate: moment(selectedDate.current).format(DEFAULT_TIME_FORMAT),
        }));
        dispatch(hideTooltip());
    }, [dispatch, data.todo_id, data.due_date_tz]);

    // Edit title handler 
    const onHeadingEdited = useCallback((title, todo_id) => {
        if (!title) { dispatch(hideTooltip()); return; }
        dispatch(updateDiary({ 
            id: todo_id,
            title
        }));
        dispatch(hideTooltip());
    }, [dispatch]);

    // REOPEN/COMPLETE diary
    const diarieActionComplete = useCallback((data) => {
        const { todo_id: id, status } = data;

        const resolvedStatus   = status === "OPEN" ? "COMPLETE" : "OPEN";
        const resolvedReferrer = status === "OPEN" ? "COMPLETED" : "REOPEN";

        dispatch(updateDiary({
            id,
            status:   resolvedStatus,
            referrer: resolvedReferrer,
        }));
    }, [dispatch]);

    const diarieActionCancelled = useCallback((id) => {
        dispatch(showDialogueBox({
            flag: true,
            component: (
                <ConfirmationTextBox
                    class="modalBody"
                    message={DIARY_CANCELLED_TEXT}
                    onConfirmClick={() => {
                        dispatch((updateDiary({
                            id,
                            status: "CANCEL",
                            referrer: "CANCELLED"
                        })));
                        dispatch(hideDialogueBox());
                    }}
                    onCancelClick={() => dispatch(hideDialogueBox())}
                    confirmationText="Confirm"
                    footerClass="modalFooter"
                    cancelText="Cancel"
                />
            ),
            overlayClassName:        "modal",
            contentClassName:        "modalContent small animated faster fadeInUpx2 confirmModal",
            shouldCloseOnOverlayClick: true,
        }));
    }, [dispatch]);

    const onHeadingClick = useCallback((data) => {
        const {todo_id, module } = data;
        const typeId = module === StringConstants.workkard ? workkard.Id : activityDetail.ActivityId;

        if (isNoteCreationVisible) {
            dispatch(discardNoteRedirectUrl({
                discardNoteRedirectUrl: createDiaryUrl(todo_id, module, typeId),
            }));
            return;
        }
        dispatch(showDialogueBox({
            flag: true,
            component: (
                <DiaryDetailPopUp
                    dataKey="detail"
                    actId={activityDetail.ActivityId || 0}
                    wkId={workkard.Id}
                    onClose={() => {
                        dispatch(hideDialogueBox());
                        dispatch(resetViewDiaryInfo());
                    }}
                    selectedDiary = {todo_id}
                />
            ),
            overlayClassName:        "modal",
            contentClassName:        "modalContent animated faster fadeInUpx2 medium",
            shouldCloseOnOverlayClick: false,
        }));
    }, [dispatch, isNoteCreationVisible, activityDetail.ActivityId, workkard.Id]);

    const onEditDiaryTitle = useCallback((e) => {
        dispatch(showTooltip({
            target:          e.target.getBoundingClientRect(),
            hideOnDocClick:  true,
            showOnClick:     true,
            isUpdatableWidth: true,
            childComponent: (
                <EditableHeading
                    isEditable
                    allowBlank
                    heading={data.title}
                    id={data.todo_id}
                    onHeadingEdited={onHeadingEdited}
                    isOpen
                    editableHeadingCancelClick={() => dispatch(hideTooltip())}
                />
            ),
        }));
    }, [dispatch, data.title, data.todo_id, onHeadingEdited]);

    const statusKey = data.status?.toLowerCase();
    const fullName  = `${data?.owner?.first_name} ${data?.owner?.last_name}`.trim();

    return (
        <div
            className="diaryListing detailListing ml__1"
            onMouseLeave={() => setActionMenuVisible(false)}
        >
            <div className="diaryRightBlock detailRightBlock">
                <div className="rowFlex">
                    <div className="diaryDescription detailDescription">
                        <div className="rowSpaceBetween mb">
                            <div className="rowFlex flexAuto diaryInformation">

                                {/* ── Complete toggle ── */}
                                {canComplete && (
                                    <div
                                        className="radioButton"
                                        onTouchStart={(e) => { e.preventDefault(); diarieActionComplete(data); }}
                                        onClick={() => diarieActionComplete(data)}
                                    >
                                        <span className={`completeCheck mr ${isCompleted ? "checked" : ""}`} />
                                    </div>
                                )}

                                <div className="colFlex">
                                    <div className="alignMiddle diaryCreatedList">
                                        {/* ── Title ── */}
                                        <div className="title alignMiddle mr">
                                            <a
                                                className={`pointer ${data.status === StringConstants.cancelled ? "diaryCanceled" : ""}`}
                                                onClick={() => onHeadingClick(data)}
                                            >
                                                {data.title}
                                            </a>
                                        </div>

                                        {!isCompleted && isEditable && !sessionContext.isAuditUser && (
                                            <a className="h__24 diaryTitle pointer ttController" onClick={onEditDiaryTitle}>
                                                <Icons name="edit" className="icon mr ml ttController" />
                                            </a>
                                        )}

                                        {data.activity_title && (
                                            <a className="status act mr pointer">{data.activity_title}</a>
                                        )}

                                        {/* ── Due date ── */}
                                        <div className="mr rowFlex alignMiddle diaryDate">
                                            {data.status === StringConstants.open && isEditable && !sessionContext.isAuditUser ? (
                                                <>
                                                    {data.is_overdue && !data.is_due_today && "due on"}
                                                    <label className="ml mr__1">
                                                        <DatePicker
                                                            customInput={<CustomDueDateLabel data={data} />}
                                                            selectedDate={dueDate}
                                                            calendarClass="change-diary-due-date-inline"
                                                            onChange={handleDateChange}
                                                            calendarClassName="animated faster fadeInUpx2"
                                                            openToDate={moment(data.due_date)}
                                                            myContainer
                                                            showTimePicker
                                                            showComment
                                                            dateFormat={DEFAULT_TIME_FORMAT}
                                                            timeIntervals={15}
                                                            shouldCloseOnSelect={false}
                                                            onConfirmClick={handleDueDateChange}
                                                            popperPlacement="bottom-start"
                                                            popperModifiers={{
                                                                flip:            { behavior: ["bottom"] },
                                                                preventOverflow: { enabled: false },
                                                                hide:            { enabled: false },
                                                            }}
                                                        />
                                                    </label>
                                                </>
                                            ) : (
                                                <label className="mr mrNone">
                                                    <span>{data.due_on}</span>
                                                </label>
                                            )}
                                        </div>

                                        {/* ── Module badge ── */}
                                        <div className="diaryWkTitle rowFlex alignMiddle">
                                            <ModuleBadge
                                                module={data.module}
                                                wkNum={data.wk_number}
                                                actTitle={data.activity_title}
                                                moduleId={data.module_id}
                                                onActivityClick={() => dispatch(getActivityDetail({ actId: data.module_id }))}
                                            />
                                        </div>
                                    </div>

                                    {/* ── Tags ── */}
                                    <div className="rowFlex mt alignMiddle flexWrap diaryStatusTags">
                                        <TagList tags={data.system_tags} />
                                        <TagList tags={data.NoSystemTags} />
                                    </div>
                                </div>
                            </div>

                            {/* ── Right actions ── */}
                            <div className="alignRight diaryActionBlock">
                                <div className="rowFlex alignRight h__24">
                                    {data.priority === 3 && (
                                        <span
                                            className="priority animated superFaster delay-5 mr quickTooltip-left"
                                            data-quicktooltip="High priority"
                                        >
                                            <Icons name="flag" className="icon" />
                                        </span>
                                    )}

                                    <label className={`status mr animated superFaster delay-5 ${getResolutionName(statusKey, true)}`}>
                                        {getResolutionName(statusKey)}
                                    </label>
                                    
                                    {!sessionContext?.isAuditUser && (
                                        <ActionMenu
                                            visible={actionMenuVisible}
                                            data={data}
                                            onToggle={() => setActionMenuVisible(p => !p)}
                                            onReopen={() => diarieActionComplete(data)}
                                            onCancel={() => diarieActionCancelled(data.todo_id)}
                                            isCompleted = {isCompleted}
                                        />
                                    )}
                                </div>

                                <div className="rowFlex rowReverse">
                                    <div className="userInfo textRight mt">
                                        {fullName}
                                        {!isActiveUser && (
                                            <span className="gray"> (Inactive)</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiarieCell;